import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { certificateService } from '@/services/certificate.service';
import { LoadingSpinner } from '@/components';
import type { CertificateVerification } from '@/types/certificate';

export default function CertificateVerifyPage() {
  const { code } = useParams<{ code: string }>();
  const [inputCode, setInputCode] = useState(code || '');
  const [result, setResult] = useState<CertificateVerification | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (code) {
      handleVerify(code);
    }
  }, [code]);

  const handleVerify = async (verificationCode: string) => {
    if (!verificationCode.trim()) return;
    setIsLoading(true);
    setHasSearched(true);
    try {
      const data = await certificateService.verify(verificationCode.trim());
      setResult(data);
    } catch {
      setResult({ valid: false });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleVerify(inputCode);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="tema-escuro-fixo min-h-screen bg-[#001420] text-white flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-center border-b border-white/10 py-6">
        <div className="flex items-center gap-3">
          <div className="size-8 text-[#FCB428]">
            <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M24 4C25.7818 14.2173 33.7827 22.2182 44 24C33.7827 25.7818 25.7818 33.7827 24 44C22.2182 33.7827 14.2173 25.7818 4 24C14.2173 22.2182 22.2182 14.2173 24 4Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <h1 className="text-xl font-bold">h.lab</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="max-w-lg w-full flex flex-col gap-8">
          <div className="text-center">
            <span className="material-symbols-outlined text-5xl text-[#FCB428] mb-4 block">
              verified
            </span>
            <h2 className="text-2xl font-bold mb-2">Verificar Certificado</h2>
            <p className="text-white/60">
              Insira o codigo de verificacao para validar a autenticidade do certificado.
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSubmit} className="flex gap-3">
            <input
              type="text"
              value={inputCode}
              onChange={(e) => setInputCode(e.target.value)}
              placeholder="Codigo de verificacao (UUID)"
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#FCB428]/50"
            />
            <button
              type="submit"
              disabled={isLoading || !inputCode.trim()}
              className="px-6 py-3 bg-[#FCB428] hover:bg-[#FFC94D] text-[#001420] rounded-xl font-bold transition-all disabled:opacity-50"
            >
              {isLoading ? <LoadingSpinner size="small" /> : 'Verificar'}
            </button>
          </form>

          {/* Results */}
          {isLoading && (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="medium" />
            </div>
          )}

          {!isLoading && hasSearched && result && result.valid && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="material-symbols-outlined text-3xl text-emerald-400">
                  check_circle
                </span>
                <h3 className="text-xl font-bold text-emerald-400">Certificado Valido</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-white/40 text-xs uppercase font-bold tracking-wider mb-1">
                    Participante
                  </p>
                  <p className="text-white text-lg font-semibold">{result.participant_name}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs uppercase font-bold tracking-wider mb-1">
                    Curso
                  </p>
                  <p className="text-white text-lg font-semibold">{result.course_name}</p>
                </div>
                <div className="flex gap-8">
                  <div>
                    <p className="text-white/40 text-xs uppercase font-bold tracking-wider mb-1">
                      Carga Horaria
                    </p>
                    <p className="text-white font-semibold">{result.hours}h</p>
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase font-bold tracking-wider mb-1">
                      Data de Emissao
                    </p>
                    <p className="text-white font-semibold">
                      {result.issued_at ? formatDate(result.issued_at) : '-'}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-white/40 text-xs uppercase font-bold tracking-wider mb-1">
                    Codigo de Verificacao
                  </p>
                  <p className="text-white/60 font-mono text-sm">{result.verification_code}</p>
                </div>
              </div>
            </div>
          )}

          {!isLoading && hasSearched && result && !result.valid && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8">
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-3xl text-red-400">
                  cancel
                </span>
                <div>
                  <h3 className="text-xl font-bold text-red-400">Certificado nao encontrado</h3>
                  <p className="text-white/50 mt-1">
                    O codigo informado nao corresponde a nenhum certificado emitido.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-auto px-6 md:px-10 py-6 border-t border-theme-border text-center">
        <p className="text-content-muted text-xs font-medium">
          &copy; 2026 h.lab por h.company.
        </p>
      </footer>
    </div>
  );
}
